// PREZENCE — My Leaves (Advanced LAF Form)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import printLAF from '../../utils/printLAF';
import { todayLocal } from '../../utils/localDate';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function MyLeaves() {
  const { user } = useAuth();
  const [leaves, setLeaves]       = useState([]);
  const [types, setTypes]         = useState([]);
  const [stats, setStats]         = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statsWarning, setStatsWarning] = useState(null);

  const initForm = {
    leave_type_id: '', from_date: '', to_date: '', reason: '',
    duty_handover_user_id: '', duty_handover_notes: '',
    section: user?.department || '', contact_during_leave: '',
    is_half_day: false, half_day_session: 'morning',
  };
  const [form, setForm] = useState(initForm);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchAll = useCallback(async () => {
    try {
      const [leavesRes, typesRes, statsRes, empsRes] = await Promise.all([
        api.get('/leaves/my'),
        api.get('/leaves/types'),
        api.get('/leaves/stats'),
        api.get('/leaves/employees-list'),
      ]);
      setLeaves(leavesRes.data.data   || []);
      setTypes(typesRes.data.data     || []);
      setStats(statsRes.data          || null);
      setEmployees(empsRes.data.data  || []);
    } catch { toast.error('Failed to load leave data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Calculate days preview
  const calcDays = useCallback(() => {
    if (form.is_half_day) return 0.5;
    if (!form.from_date || !form.to_date) return 0;
    let d = 0;
    const c = new Date(form.from_date);
    const e = new Date(form.to_date);
    while (c <= e) { if (c.getDay() !== 0 && c.getDay() !== 6) d++; c.setDate(c.getDate()+1); }
    return d;
  }, [form.is_half_day, form.from_date, form.to_date]);
  const daysPreview = calcDays();

  // Show warning when leave type or dates selected
  useEffect(() => {
    if (!form.leave_type_id || !stats) { setStatsWarning(null); return; }
    const typeStat = stats.stats?.find(s => s.leave_type_id === form.leave_type_id);
    if (!typeStat) return;
    const days = calcDays();
    if (days > 0 && typeStat.balance < days) {
      setStatsWarning({ type: 'error', message: `⚠️ Insufficient balance. You have only ${typeStat.balance} day(s) left for ${typeStat.name}.` });
    } else if (stats.totalMonthTaken > 0) {
      setStatsWarning({ type: 'info', message: `📊 You have already taken ${stats.totalMonthTaken} day(s) this month and ${stats.totalYearTaken} day(s) this year. This may affect your annual performance review.` });
    } else {
      setStatsWarning(null);
    }
  }, [form.leave_type_id, form.from_date, form.to_date, form.is_half_day, stats, calcDays]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.leave_type_id || !form.from_date || !form.to_date || !form.reason) {
      toast.error('Please fill all required fields'); return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/leaves/apply', form);
      const sm = res.data.stats_message;
      toast.success('Leave application submitted!');
      // Show stats popup
      if (sm) {
        const msg = `Application submitted!\n📅 This year: ${sm.year_taken + daysPreview} days taken\n📆 Balance remaining: ${Math.max(0, sm.balance_left)} days`;
        setTimeout(() => toast(`${msg}`, { duration: 6000, icon: '📋' }), 500);
      }
      setShowModal(false);
      setForm(initForm);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit application');
    } finally { setSubmitting(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave application?')) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success('Leave cancelled');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to cancel'); }
  };

  const statusBadge = s => ({ approved:'badge-green', rejected:'badge-red', pending:'badge-amber', cancelled:'badge-gray' })[s]||'badge-gray';
  const printBtn = (l) => l.status === 'approved' ? (
    <button className="btn btn-secondary btn-sm" style={{ marginLeft:8 }} title="Print Leave Application Form"
      onClick={() => printLAF(l, null)}>🖨 Print LAF</button>
  ) : null;
  const today = todayLocal();

  if (loading) return <div style={{ display:'flex',justifyContent:'center',padding:60 }}><div className="spinner" style={{width:28,height:28}} /></div>;

  return (
    <div style={{ animation:'fadeIn 0.4s ease', maxWidth:960, margin:'0 auto' }}>
      {/* Header */}
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 className="page-title">My Leaves</h1>
          <p className="page-sub">Apply for leave, track your balance and application history</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowModal(true); setForm(initForm); }}>
          + Apply for Leave
        </button>
      </div>

      {/* Leave Balance Table — matches LAF form */}
      {stats?.stats?.length > 0 && (
        <div className="card" style={{ marginBottom:24 }}>
          <div className="card-header">
            <span className="card-title">LEAVE STATEMENT — {stats.year}</span>
            <span style={{ fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'" }}>
              Month: {MONTHS[stats.month-1]} · Year: {stats.year}
            </span>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid rgba(56,189,248,0.1)', background:'rgba(56,189,248,0.05)' }}>
                  {['Leave Type','Paid','Total Allocated','Availed (Year)','This Month','Balance','Status'].map(h => (
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:10, color:'#475569', fontWeight:700, letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", whiteSpace:'nowrap' }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.stats.map((s, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding:'11px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <div style={{ width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0 }} />
                        <span style={{ fontSize:13, fontWeight:600, color:'#e2e8f0' }}>{s.name}</span>
                      </div>
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <span className={`badge ${s.is_paid ? 'badge-green' : 'badge-red'}`} style={{ fontSize:9 }}>
                        {s.is_paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td style={{ padding:'11px 14px', fontFamily:"'JetBrains Mono'", fontSize:13, color:'#94a3b8', fontWeight:600 }}>{s.allocated || s.days_allowed}</td>
                    <td style={{ padding:'11px 14px', fontFamily:"'JetBrains Mono'", fontSize:13, color:'#f59e0b' }}>{s.availed}</td>
                    <td style={{ padding:'11px 14px', fontFamily:"'JetBrains Mono'", fontSize:13, color:'#3ba8ff' }}>{s.month_taken}</td>
                    <td style={{ padding:'11px 14px' }}>
                      <span style={{ fontFamily:"'JetBrains Mono'", fontSize:14, fontWeight:800, color:s.balance <= 2 ? '#ef4444' : s.balance <= 5 ? '#f59e0b' : '#22c55e' }}>
                        {s.balance}
                      </span>
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <div className="progress-bar-wrap" style={{ width:80 }}>
                        <div className="progress-bar-fill" style={{ width:`${s.allocated > 0 ? Math.min(100,(s.availed/s.allocated)*100) : 0}%`, background:s.color }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop:'2px solid rgba(56,189,248,0.15)', background:'rgba(56,189,248,0.04)' }}>
                  <td colSpan={3} style={{ padding:'10px 14px', fontSize:12, fontWeight:700, color:'#94a3b8' }}>TOTAL</td>
                  <td style={{ padding:'10px 14px', fontFamily:"'JetBrains Mono'", fontSize:13, fontWeight:800, color:'#f59e0b' }}>{stats.totalYearTaken}</td>
                  <td style={{ padding:'10px 14px', fontFamily:"'JetBrains Mono'", fontSize:13, fontWeight:800, color:'#3ba8ff' }}>{stats.totalMonthTaken}</td>
                  <td colSpan={2} style={{ padding:'10px 14px' }} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Applications list */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">MY APPLICATIONS</span>
          <span style={{ fontSize:11, color:'#475569' }}>{leaves.length} total</span>
        </div>
        {leaves.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-text">No applications yet. Click "Apply for Leave" to get started.</div></div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {leaves.map(l => (
              <div key={l.id} style={{
                background:'rgba(255,255,255,0.02)',
                border:`1px solid ${l.status==='pending'?'rgba(245,158,11,0.2)':l.status==='approved'?'rgba(34,197,94,0.15)':l.status==='rejected'?'rgba(239,68,68,0.15)':'rgba(255,255,255,0.05)'}`,
                borderRadius:12, padding:'16px 18px',
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <div style={{ width:9,height:9,borderRadius:'50%',background:l.leave_types?.color||'#63dcbc' }} />
                        <span style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>{l.leave_types?.name || 'Leave'}</span>
                      </div>
                      <span className={`badge ${statusBadge(l.status)}`}>{l.status.charAt(0).toUpperCase()+l.status.slice(1)}</span>{printBtn(l)}
                      {l.is_half_day && <span className="badge badge-blue" style={{ fontSize:9 }}>Half Day ({l.half_day_session})</span>}
                    </div>
                    <div style={{ display:'flex', gap:20, flexWrap:'wrap', marginBottom:6 }}>
                      <div style={{ fontSize:12, color:'#64748b' }}>
                        📅 {new Date(l.from_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                        {l.from_date !== l.to_date && ` → ${new Date(l.to_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}`}
                        <span style={{ color:'#63dcbc', fontWeight:700, marginLeft:6, fontFamily:"'JetBrains Mono'" }}>({l.total_days}d)</span>
                      </div>
                      <div style={{ fontSize:12, color:'#64748b' }}>
                        📝 Applied: {new Date(l.applied_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                      </div>
                    </div>
                    <div style={{ fontSize:12, color:'#94a3b8', fontStyle:'italic', marginBottom:l.duty_handover_user||l.review_notes?6:0 }}>
                      Reason: "{l.reason}"
                    </div>
                    {l.duty_handover_user && (
                      <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>
                        👤 Duty delegated to: <span style={{ color:'#94a3b8', fontWeight:600 }}>{l.duty_handover_user.full_name}</span>
                        {l.duty_handover_notes && ` — ${l.duty_handover_notes}`}
                      </div>
                    )}
                    {l.review_notes && (
                      <div style={{ marginTop:8, padding:'8px 12px', background:l.status==='approved'?'rgba(34,197,94,0.06)':'rgba(239,68,68,0.06)', borderRadius:8, borderLeft:`3px solid ${l.status==='approved'?'#22c55e':'#ef4444'}` }}>
                        <div style={{ fontSize:9, color:'#475569', fontFamily:"'JetBrains Mono'", marginBottom:3 }}>HR/ADMIN NOTE</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>{l.review_notes}</div>
                        {l.reviewer && <div style={{ fontSize:10, color:'#334155', marginTop:3 }}>— {l.reviewer.full_name} · {new Date(l.reviewed_at).toLocaleDateString('en-IN')}</div>}
                      </div>
                    )}
                  </div>
                  {l.status === 'pending' && (
                    <button className="btn btn-danger btn-sm" onClick={() => handleCancel(l.id)}>Cancel</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── LEAVE APPLICATION MODAL — Digital LAF ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div style={{
            background:'#0d1520', border:'1px solid rgba(56,189,248,0.2)',
            borderRadius:20, padding:'32px 36px', width:'100%', maxWidth:680,
            maxHeight:'90vh', overflowY:'auto',
            boxShadow:'0 32px 80px rgba(0,0,0,0.6)',
            animation:'slideUp 0.3s cubic-bezier(.34,1.56,.64,1)',
          }} onClick={e => e.stopPropagation()}>

            {/* LAF Header */}
            <div style={{ textAlign:'center', marginBottom:28, paddingBottom:20, borderBottom:'1px solid rgba(56,189,248,0.12)' }}>
              <div style={{ fontSize:9, color:'#3ba8ff', letterSpacing:'0.25em', fontFamily:"'JetBrains Mono'", marginBottom:6 }}>PRELEGANT TECHNOLOGIES PRIVATE LIMITED</div>
              <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:'#f0f6ff', marginBottom:4 }}>
                Leave Application Form
              </h2>
              <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'" }}>LAF · {new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
            </div>

            {/* Stats warning */}
            {statsWarning && (
              <div style={{
                padding:'12px 16px', borderRadius:10, marginBottom:20,
                background: statsWarning.type==='error' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
                border: `1px solid ${statsWarning.type==='error' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
                fontSize:12, color: statsWarning.type==='error' ? '#ef4444' : '#f59e0b', lineHeight:1.5,
              }}>{statsWarning.message}</div>
            )}

            <form onSubmit={handleApply}>
              {/* Employee info (auto-filled) */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:12, padding:'16px 18px', marginBottom:20 }}>
                <div style={{ fontSize:10, color:'#3ba8ff', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:12 }}>EMPLOYEE INFORMATION</div>
                <div className="grid-2">
                  <div>
                    <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", marginBottom:4 }}>NAME</div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0' }}>{user?.full_name}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", marginBottom:4 }}>DESIGNATION</div>
                    <div style={{ fontSize:13, color:'#94a3b8' }}>{user?.designation || '—'}</div>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">SECTION</label>
                    <input className="form-input" value={form.section} onChange={e => set('section', e.target.value)} placeholder="e.g. Development" />
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", marginBottom:4 }}>DEPARTMENT</div>
                    <div style={{ fontSize:13, color:'#94a3b8' }}>{user?.department || '—'}</div>
                  </div>
                </div>
              </div>

              {/* Leave Type — checkbox grid like LAF */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:12, padding:'16px 18px', marginBottom:20 }}>
                <div style={{ fontSize:10, color:'#3ba8ff', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:12 }}>TYPE OF LEAVE (Select One)</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
                  {types.map(t => (
                    <label key={t.id} style={{
                      display:'flex', alignItems:'center', gap:10, cursor:'pointer',
                      padding:'10px 12px', borderRadius:9,
                      background: form.leave_type_id===t.id ? `rgba(${t.color?.replace('#','')},0.1)` : 'rgba(255,255,255,0.02)',
                      border: `1.5px solid ${form.leave_type_id===t.id ? t.color||'#63dcbc' : 'rgba(255,255,255,0.07)'}`,
                      transition:'all 0.15s',
                    }}>
                      <input type="radio" name="leave_type" value={t.id} checked={form.leave_type_id===t.id} onChange={() => set('leave_type_id', t.id)} style={{ accentColor: t.color||'#63dcbc' }} />
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color: form.leave_type_id===t.id ? (t.color||'#63dcbc') : '#94a3b8' }}>{t.name}</div>
                        <div style={{ fontSize:10, color:'#475569' }}>{t.days_allowed > 0 ? `${t.days_allowed} days/year` : 'Unpaid'}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {/* Half day option */}
                <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:10 }}>
                  <input type="checkbox" id="halfday" checked={form.is_half_day} onChange={e => set('is_half_day', e.target.checked)} style={{ accentColor:'#63dcbc', width:14, height:14 }} />
                  <label htmlFor="halfday" style={{ fontSize:12, color:'#94a3b8', cursor:'pointer' }}>Half Day Leave</label>
                  {form.is_half_day && (
                    <select className="form-select" value={form.half_day_session} onChange={e => set('half_day_session', e.target.value)} style={{ width:'auto', padding:'4px 10px', fontSize:12 }}>
                      <option value="morning">Morning Session</option>
                      <option value="afternoon">Afternoon Session</option>
                    </select>
                  )}
                </div>
              </div>

              {/* Period */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:12, padding:'16px 18px', marginBottom:20 }}>
                <div style={{ fontSize:10, color:'#3ba8ff', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:12 }}>PERIOD OF LEAVE</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:12, alignItems:'end' }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">FROM DATE *</label>
                    <input className="form-input" type="date" value={form.from_date} onChange={e => set('from_date', e.target.value)} min={today} required />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">TO DATE *</label>
                    <input className="form-input" type="date" value={form.to_date} onChange={e => set('to_date', e.target.value)} min={form.from_date||today} required />
                  </div>
                  <div style={{ padding:'10px 16px', background:'rgba(99,220,188,0.1)', border:'1px solid rgba(99,220,188,0.25)', borderRadius:9, textAlign:'center', minWidth:70 }}>
                    <div style={{ fontSize:22, fontWeight:800, color:'#63dcbc', fontFamily:"'JetBrains Mono'", lineHeight:1 }}>{daysPreview}</div>
                    <div style={{ fontSize:9, color:'#475569', marginTop:2 }}>days</div>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="form-group">
                <label className="form-label">REASON OF LEAVE *</label>
                <textarea className="form-textarea" value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Please describe the reason for your leave application..." style={{ minHeight:80 }} required />
              </div>

              {/* Contact during leave */}
              <div className="form-group">
                <label className="form-label">CONTACT DURING LEAVE (Phone / Email)</label>
                <input className="form-input" value={form.contact_during_leave} onChange={e => set('contact_during_leave', e.target.value)} placeholder="+880 1xxx-xxxxxx or email@example.com" />
              </div>

              {/* Duty handover */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:12, padding:'16px 18px', marginBottom:20 }}>
                <div style={{ fontSize:10, color:'#3ba8ff', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono'", marginBottom:12 }}>DUTY DELEGATION (Duties will be carried out by)</div>
                <div className="form-group" style={{ marginBottom:12 }}>
                  <label className="form-label">SELECT COLLEAGUE</label>
                  <select className="form-select" value={form.duty_handover_user_id} onChange={e => set('duty_handover_user_id', e.target.value)}>
                    <option value="">Select colleague who will cover your duties</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id}>{e.full_name} — {e.designation || e.department}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">HANDOVER NOTES</label>
                  <textarea className="form-textarea" value={form.duty_handover_notes} onChange={e => set('duty_handover_notes', e.target.value)} placeholder="Key tasks, passwords, pending items to be aware of..." style={{ minHeight:60 }} />
                </div>
              </div>

              {/* Submit */}
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8, paddingTop:16, borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting || !form.leave_type_id}>
                  {submitting ? <><div className="spinner" style={{width:14,height:14}} /> Submitting...</> : '📋 Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
